import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { PageBody } from "../PageBody";
import { Typography } from "../../atoms/Typography";
import { defaultTheme } from "../../themes";
import { Label } from "../../atoms/Label";

type ItemProps = {
  width?: string;
};

type Product = {
  code: string;
  name: string;
  description: string;
  unitSize: number;
};

type DeliveryTime = {
  code: string;
  section: string;
};

export type DeliveryProduct = {
  product: Product;
  quantity: number;
};

export type DeliveryOrder = {
  name?: string;
  postalCode?: string;
  phoneNumber?: string;
  address?: string;
  deliveryDate?: string;
  deliveryTime?: DeliveryTime;
  deliveryProducts?: DeliveryProduct[];
  orderClass?: string;
};

export type CompleteDeliveryOrderPageProps = {
  deliveryOrder: DeliveryOrder;
  onDeliveryOrderCompleteClick: () => void;
};

const ContentWrapper = styled.div`
  padding: 0 40px;
  clear: both;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 80px;
`;

const InputWrapper = styled.div`
  display: flex;
  width: 100%;
`;

const LeftBlock = styled.div`
  padding: 15px 0 0 0;
  width: 85%;
`;

const RightBlock = styled.div`
  flex: 1;
  padding: 15px 0 0 0;
`;

const RowWrapper = styled.div`
  min-height: 58px;
  padding: 10px 5px 10px 0;
  ${({ theme }) => `border-bottom: 1px solid ${theme.colors.primaryBorder};`}
  p {
    margin-top: 10px;
  }
`;

const SomeItemRowWrapper = styled(RowWrapper)`
  display: flex;
`;

const DeliveryItemRowWrapper = styled(RowWrapper)`
  border-bottom: none;
`;

const ItemWrapper = styled.div<ItemProps>`
  width: ${(props) => props.width || "30%"};
  margin-right: 15px;
`;

const DeliveryItemWrapper = styled.div`
  display: flex;
  padding: 10px 5px 10px 0;
  ${({ theme }) => `border-bottom: 1px solid ${theme.colors.primaryBorder};`}

  &:last-child {
    border: 0;
  }
`;

export const CompleteDeliveryOrderPage = ({
  deliveryOrder,
  onDeliveryOrderCompleteClick,
}: CompleteDeliveryOrderPageProps) => {
  return (
    <>
      <PageBody
        title={"発送依頼を行いました"}
        buttonTitle="完了"
        buttonOutline
        buttonIcon={
          <FontAwesomeIcon icon={"check"} color={defaultTheme.colors.brand} />
        }
        onButtonClick={() => {
          onDeliveryOrderCompleteClick();
        }}
      >
        <Container>
          <ContentWrapper>
            <RowWrapper>
              <Label text={"宛名"} />
              <InputWrapper>
                <Typography size={2}>{deliveryOrder.name}</Typography>
              </InputWrapper>
            </RowWrapper>
            <SomeItemRowWrapper>
              <ItemWrapper width="10%">
                <Label text={"郵便番号"} />
                <InputWrapper>
                  <Typography size={2}>{deliveryOrder.postalCode}</Typography>
                </InputWrapper>
              </ItemWrapper>

              <ItemWrapper width="50%">
                <Label text={"配送先住所"} />
                <InputWrapper>
                  <Typography size={2}>{deliveryOrder.address}</Typography>
                </InputWrapper>
              </ItemWrapper>

              <ItemWrapper width="10%">
                <Label text={"電話番号"} />
                <InputWrapper>
                  <Typography size={2}>{deliveryOrder.phoneNumber}</Typography>
                </InputWrapper>
              </ItemWrapper>
            </SomeItemRowWrapper>
            <SomeItemRowWrapper>
              <ItemWrapper width="10%">
                <Label text={"配送指定日"} />
                <InputWrapper>
                  <Typography size={2}>
                    {deliveryOrder.deliveryDate || "-"}
                  </Typography>
                </InputWrapper>
              </ItemWrapper>

              <ItemWrapper width="50%">
                <Label text={"配送時間帯"} />
                <InputWrapper>
                  <Typography size={2}>
                    {deliveryOrder.deliveryTime?.section}
                  </Typography>
                </InputWrapper>
              </ItemWrapper>

              <ItemWrapper width="20%">
                <Label text={"注文クラス"} />
                <InputWrapper>
                  <Typography size={2}>{deliveryOrder.orderClass}</Typography>
                </InputWrapper>
              </ItemWrapper>
            </SomeItemRowWrapper>
            <DeliveryItemRowWrapper>
              <Label text="配送物" />
              {deliveryOrder.deliveryProducts
                ? deliveryOrder.deliveryProducts.map(
                    (deliveryProduct, index) => (
                      <DeliveryItemWrapper key={index}>
                        <LeftBlock>
                          {`${deliveryProduct.product.name} : ${deliveryProduct.product.description}`}
                        </LeftBlock>
                        <RightBlock>{deliveryProduct.quantity}個</RightBlock>
                      </DeliveryItemWrapper>
                    ),
                  )
                : ""}
            </DeliveryItemRowWrapper>
          </ContentWrapper>
        </Container>
      </PageBody>
    </>
  );
};
